import { GoogleOAuthProvider } from '@react-oauth/google';
import React, { FC, Suspense, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { BrowserRouter, matchPath, Route, Routes, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';
import AppSubscriptionCheck from '../components/appSubscriptionCheck';
import ErrorPageBoundary from '../components/pages/_error';
import NotFoundPage from '../components/pages/_notFound';
import { SIDEBAR_COLLAPSE_EVENT } from '../components/widgets/inlineHelpLayout/inlineHelpLayout';
import { AgentAssistantLauncher, AgentAssistantProvider } from '../components/widgets/agentAssistant';
import LeftAppSidebar from '../components/widgets/leftAppSidebar/AppLeftSidebar';
import { APP_SIDEBAR_COLLAPSED_WIDTH, APP_SIDEBAR_WIDTH } from '../components/widgets/leftAppSidebar/leftAppSidebarStyles';
import { getQuickAccessEnvironment, type LeftAppSidebarState } from '../components/widgets/leftAppSidebar/leftAppSidebarTypes';
import { StarredMenuProvider } from '../components/widgets/leftAppSidebar/StarredMenu';
import { LoadScripts } from '../components/widgets/scriptsLoader/loadScripts';
import { AppStateProvider, useAppState } from '../shared/appState';
import config from '../shared/config';
import { PageLoading } from '../shared/globals';
import { backofficeUrl, getJWTFromQueryParams, isVtoPage, routeMatchUrl, setCookie } from '../shared/helpers';
import { PageTransitionProvider, usePageTransition } from '../shared/navigationTransition';
import routes from '../shared/routes';
import { defaultLayoutAdminUserQuery } from './__generated__/defaultLayoutAdminUserQuery.graphql';
import { DefaultLayoutContainer } from './defaultLayoutContainer';

const AppLayoutGlobalStyles = createGlobalStyle<{ $isHeaderHidden: boolean }>`
    :root {
        --help-panel-width: 0px;
        --page-footer-buttons-height: 20px;
    }
    ${({ $isHeaderHidden }) =>
        $isHeaderHidden &&
        css`
            body {
                padding-top: 0 !important;
            }
        `}`;

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'zakeke.leftSidebar.collapsed';

const getInitialSidebarCollapsed = () => {
    if (typeof window === 'undefined') return false;
    try {
        return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const AppShell = styled.div<{ $sidebarWidth: number }>`
    --app-sidebar-width: ${(props) => props.$sidebarWidth}px;
    min-height: 100vh;
`;

const AppContent = styled.div<{ $isMenuHidden: boolean; $isSidebarCollapsed: boolean; $isEmbedded: boolean }>`
    position: relative;
    min-height: 100vh;
    margin-left: ${(props) => (props.$isMenuHidden ? '0' : `${props.$isSidebarCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH}px`)};
    /* Embedded tools hide the menu but are still a page: they keep the gutter, otherwise page-level
       elements that are not cards (upgrade banners) would sit flush against the frame edges.
       Fullscreen tools, which hide the menu without being embedded, stay edge to edge. */
    padding-top: ${(props) => (props.$isEmbedded ? '24px' : '0')};
    padding-right: ${(props) => (props.$isMenuHidden && !props.$isEmbedded ? '0' : '48px')};
    padding-bottom: var(--page-footer-buttons-height, 0px);
    padding-left: ${(props) => (props.$isMenuHidden && !props.$isEmbedded ? '0' : '48px')};
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
    transition: margin-left 0.2s ease;
`;

const PageTransitionOverlay = styled.div<{ $isMenuHidden: boolean; $isSidebarCollapsed: boolean; $isVisible: boolean }>`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: ${(props) => (props.$isMenuHidden ? '0' : `${props.$isSidebarCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH}px`)};
    z-index: 17;
    background: rgba(249, 249, 249, 0.68);
    opacity: ${(props) => (props.$isVisible ? 1 : 0)};
    pointer-events: ${(props) => (props.$isVisible ? 'all' : 'none')};
    transition:
        left 0.2s ease,
        opacity 0.16s ease;
`;

const PageBody = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
`;

const AppLayout: FC = () => {
    return (
        <PageTransitionProvider>
            <AppLayoutContent />
        </PageTransitionProvider>
    );
};

const AppLayoutContent: FC = () => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(getInitialSidebarCollapsed);
    const { isPageTransitionPending } = usePageTransition();
    const theme = useTheme();
    const location = useLocation();
    const { state } = useAppState();
    const starredEnvironment = getQuickAccessEnvironment(state.lastUsedTool);
    const currentRoute = routes.find((x) => {
        const mathWithParams = matchPath(x.url.replaceAll('?', ''), location.pathname);

        // Remove all optioanl params from x.url
        const urlWithNoOptionalParams = x.url.replace(/:\w+\?/g, '');

        const matchNoParams = matchPath(urlWithNoOptionalParams, location.pathname + '/');

        return matchNoParams || mathWithParams;
    });

    const isEmbedded = new URLSearchParams(location.search).get('embedded') === 'true';
    const isHeaderHidden = (isEmbedded || currentRoute?.isHeaderHidden || theme.additionalProperties?.isToolbarHidden) ?? false;
    const isMenuHidden = (isEmbedded || currentRoute?.isMenuHidden || theme.additionalProperties?.isSidebarHidden) ?? false;
    const forceTools = currentRoute?.forceTools ?? false;
    const sidebarWidth = isMenuHidden ? 0 : isSidebarCollapsed ? APP_SIDEBAR_COLLAPSED_WIDTH : APP_SIDEBAR_WIDTH;
    const RouteSkeleton = currentRoute?.skeleton;
    const routeFallback = RouteSkeleton ? <RouteSkeleton /> : <PageLoading />;

    const savedCollapseStateRef = useRef(false);
    const previousIsMenuHiddenRef = useRef(isMenuHidden);
    const sidebarStateRef = useRef<LeftAppSidebarState | null>(null);
    const sidebarInitialState = previousIsMenuHiddenRef.current && !isMenuHidden ? sidebarStateRef.current : null;

    useEffect(() => {
        previousIsMenuHiddenRef.current = isMenuHidden;
    }, [isMenuHidden]);

    useEffect(() => {
        const handler = (e: Event) => {
            const { collapsed } = (e as CustomEvent<{ collapsed: boolean }>).detail;
            if (collapsed) {
                setIsSidebarCollapsed((prev) => {
                    savedCollapseStateRef.current = prev;
                    return true;
                });
            } else {
                setIsSidebarCollapsed(savedCollapseStateRef.current);
            }
        };
        window.addEventListener(SIDEBAR_COLLAPSE_EVENT, handler);
        return () => window.removeEventListener(SIDEBAR_COLLAPSE_EVENT, handler);
    }, []);

    const handleSidebarCollapsedChange = (isCollapsed: boolean) => {
        setIsSidebarCollapsed(isCollapsed);
        try {
            window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, isCollapsed ? 'true' : 'false');
        } catch {
            return;
        }
    };

    return (
        <>
            <AppLayoutGlobalStyles $isHeaderHidden={isHeaderHidden} />
            <AgentAssistantProvider>
                <StarredMenuProvider environment={starredEnvironment}>
                    <AppShell $sidebarWidth={sidebarWidth}>
                        {!isMenuHidden && (
                            <LeftAppSidebar
                                isMenuHidden={isMenuHidden}
                                isCollapsed={isSidebarCollapsed}
                                onCollapsedChange={handleSidebarCollapsedChange}
                                initialState={sidebarInitialState}
                                onStateChange={(sidebarState) => {
                                    sidebarStateRef.current = sidebarState;
                                }}
                            />
                        )}
                        <AppContent $isMenuHidden={isMenuHidden} $isSidebarCollapsed={isSidebarCollapsed} $isEmbedded={isEmbedded}>
                            <PageTransitionOverlay
                                $isMenuHidden={isMenuHidden}
                                $isSidebarCollapsed={isSidebarCollapsed}
                                $isVisible={isPageTransitionPending}
                                aria-hidden='true'
                            />
                            {/* We remove all :params to avoid losing the context for some sub pages like products */}
                            <Suspense fallback={routeFallback}>
                                <Routes>
                                    {routes.map((route, index) => {
                                        return (
                                            <Route
                                                key={index}
                                                path={routeMatchUrl(route.url)}
                                                element={
                                                    <PageBody>
                                                        <ErrorPageBoundary key={route.url.replace(/:\w+/g, '')}>
                                                            {React.createElement(route.component)}
                                                        </ErrorPageBoundary>
                                                    </PageBody>
                                                }
                                            />
                                        );
                                    })}
                                    <Route path='*' element={<NotFoundPage />} />
                                </Routes>
                            </Suspense>
                        </AppContent>
                    </AppShell>
                    {!isEmbedded && <AgentAssistantLauncher />}
                </StarredMenuProvider>
            </AgentAssistantProvider>
        </>
    );
};

const localizationsScripts = [
    config.publicPath + 'assets/libs/gettext/gettext.js',
    config.localizationsUrl + config.culture.toLowerCase().substring(0, 2) + '/admin.js'
];

const DefaultLayoutContent: FC = () => {
    // This is necessary because the / route is managed by DefaultLayout, which is the seller layout.
    // For administrator-only users, this is where we can validate admin status as soon as possible and redirect to the correct page.
    const result = useLazyLoadQuery<defaultLayoutAdminUserQuery>(
        graphql`
            query defaultLayoutAdminUserQuery {
                isAdminUser
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );
    const adminConsoleUrl = backofficeUrl('/Console');

    useEffect(() => {
        if (result.isAdminUser) {
            window.location.href = adminConsoleUrl;
        }
    }, [adminConsoleUrl, result.isAdminUser]);

    if (result.isAdminUser) return null;

    return (
        <LoadScripts scriptUrls={localizationsScripts} fallback={<PageLoading />}>
            <GoogleOAuthProvider clientId={config.googleLoginClientID}>
                <BrowserRouter>
                    <Suspense fallback={<PageLoading />}>
                        <AppStateProvider>
                            <AppSubscriptionCheck>
                                <DefaultLayoutContainer>
                                    <AppLayout />
                                </DefaultLayoutContainer>
                            </AppSubscriptionCheck>
                        </AppStateProvider>
                    </Suspense>
                </BrowserRouter>
            </GoogleOAuthProvider>
        </LoadScripts>
    );
};

const DefaultLayout: FC = () => {
    if (!config.token) {
        const jwt = getJWTFromQueryParams();
        if (isVtoPage() && jwt) {
            config.token = jwt;
            setCookie('zakeke_token', jwt, 365);
        } else {
            window.location.href = `${backofficeUrl('/Login')}`;
            return null;
        }
    }

    return (
        <Suspense fallback={<PageLoading />}>
            <DefaultLayoutContent />
        </Suspense>
    );
};

export default DefaultLayout;
